import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { NumericFormat } from 'react-number-format'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box, FormControl, MenuItem, Select, TextField, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { useDebouncedCallback } from 'use-debounce'

import { quoteItemsApi, useGetQuoteItemsQuery, useLazyGetQuoteItemsQuery } from '@/app/services/quoteItems'
import { selectLocale } from '@/app/slices/appSlice'
import { selectCurrencyCode, selectOrganisationId } from '@/app/slices/organisationSlice'
import { selectIsCalculatingQuote } from '@/app/slices/quoteItemsSlice'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'
import { useUpdateQuoteItem } from '@/common/hooks/useUpdateQuoteItem'
import { getCurrencyFormat } from '@/common/utils'

const classes = {
    foldingSection: {
        width: '100%',
        gap: '12px',
    },
    bold: {
        fontWeight: 'bold',
    },
    itemColumn: {
        display: 'flex',
        flex: 1,
        fontSize: '14px',
    },
    formControl_medium: {
        width: '120px',
    },
    formControl_small: {
        width: '80px',
    },
}

const ItemRow = ({ children }) => (
    <Box
        sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
        }}
    >
        {children}
    </Box>
)

const ItemFolding = ({ quoteItemId }) => {
    const { t } = useTranslation()
    const { quoteId } = useParams()
    const dispatch = useDispatch()

    const organisationId = useSelector(selectOrganisationId)
    const locale = useSelector(selectLocale)
    const currencyCode = useSelector(selectCurrencyCode)
    const isCalculatingQuote = useSelector(selectIsCalculatingQuote)

    const { quoteItem } = useGetQuoteItemsQuery(
        {
            organisationId,
            quoteId,
        },
        {
            selectFromResult: ({ data }) => ({
                quoteItem: data?.find((item) => item.id === quoteItemId),
            }),
        }
    )

    const [getQuoteItems] = useLazyGetQuoteItemsQuery()
    const { handleUpdateQuoteItem, markItemPending } = useUpdateQuoteItem()

    const units = quoteItem?.isImperial ? 'in' : 'mm'

    const isPartFromLibrary = Boolean(quoteItem?.partLibraryEntryId)

    const currencyFormat = getCurrencyFormat(currencyCode, locale)

    const debounceHandleUpdateQuoteItem = useDebouncedCallback(handleUpdateQuoteItem, 800)

    const handleSourceChange = async (event) => {
        const value = event?.target?.value
        if (!value || quoteItem?.foldingSource === value) return

        markItemPending(quoteItemId)

        await handleUpdateQuoteItem({
            ...quoteItem,
            foldingSource: value,
        })

        if (value === 'Drawing') {
            await getQuoteItems({ organisationId, quoteId })
        }
    }

    const handleMaxLengthChange = (values) => {
        const { floatValue } = values

        markItemPending(quoteItemId)
        debounceHandleUpdateQuoteItem.cancel()

        // Immediate cache update for responsive UI
        dispatch(
            // @ts-ignore
            quoteItemsApi.util.updateQueryData('getQuoteItems', { organisationId, quoteId }, (draft) => {
                const item = draft.find((i) => i.id === quoteItemId)
                if (item) {
                    item.maxFoldLength = floatValue
                }
            })
        )

        debounceHandleUpdateQuoteItem({
            ...quoteItem,
            maxFoldLength: floatValue,
            numberOfFolds: quoteItem?.numberOfFolds,
        })
    }

    const handleNumberOfFoldsChange = (values) => {
        const { floatValue } = values

        markItemPending(quoteItemId)
        debounceHandleUpdateQuoteItem.cancel()

        // Immediate cache update for responsive UI
        dispatch(
            // @ts-ignore
            quoteItemsApi.util.updateQueryData('getQuoteItems', { organisationId, quoteId }, (draft) => {
                const item = draft.find((i) => i.id === quoteItemId)
                if (item) {
                    item.numberOfFolds = floatValue
                }
            })
        )

        debounceHandleUpdateQuoteItem({
            ...quoteItem,
            numberOfFolds: floatValue,
            maxFoldLength: quoteItem?.maxFoldLength,
        })
    }

    return (
        <Box
            sx={[
                {
                    alignItems: 'flex-start',
                    display: 'flex',
                    flex: '1',
                    flexDirection: 'column',
                },
                classes.foldingSection,
            ]}
        >
            <ItemRow>
                <Typography
                    data-testid="quote-item-folding-section-title"
                    sx={classes.bold}
                >
                    {t('Folding')}
                </Typography>
            </ItemRow>
            <ItemRow>
                <Typography
                    data-testid="nqp-quote-item-source-label"
                    sx={classes.itemColumn}
                >
                    {t('Source')}
                </Typography>
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <FormControl
                        color="primary"
                        margin="none"
                        size="small"
                        sx={classes.formControl_medium}
                        variant="outlined"
                    >
                        <Select
                            data-testid="nqp-quote-item-source"
                            disabled={isPartFromLibrary || isCalculatingQuote}
                            id="select-itemSource"
                            labelId="select-itemSource-label"
                            sx={{
                                '& .MuiSelect-select': {
                                    fontSize: '14px',
                                    paddingBlock: '5px',
                                    paddingInline: '12px',
                                },
                            }}
                            value={quoteItem?.foldingSource || ''}
                            onChange={handleSourceChange}
                        >
                            <MenuItem
                                key={'Drawing'}
                                value={'Drawing'}
                            >
                                {t('Drawing')}
                            </MenuItem>
                            <MenuItem
                                key={'Manual'}
                                value={'Manual'}
                            >
                                {t('Manual')}
                            </MenuItem>
                        </Select>
                    </FormControl>
                </TbxTooltip>
            </ItemRow>
            <ItemRow>
                <Typography
                    data-testid="nqp-quote-item-max-length-label"
                    sx={classes.itemColumn}
                >
                    {t('Max length')}
                </Typography>
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <NumericFormat
                        allowNegative={false}
                        customInput={TextField}
                        decimalSeparator={currencyFormat.decimal}
                        disabled={quoteItem?.foldingSource === 'Drawing' || isPartFromLibrary || isCalculatingQuote}
                        error={quoteItem?.numberOfFolds > 0 ? quoteItem?.maxFoldLength <= 0 : null}
                        inputProps={{
                            style: { fontSize: '14px', textAlign: 'right', paddingBlock: '6px', paddingInline: '12px' },
                            'data-testid': 'nqp-quote-item-max-length-quantity',
                        }}
                        placeholder={`0 ${units}`}
                        size="small"
                        suffix={` ${units}`}
                        sx={classes.formControl_medium}
                        thousandSeparator={currencyFormat.group}
                        value={quoteItem?.maxFoldLength ?? ''}
                        variant="outlined"
                        onValueChange={handleMaxLengthChange}
                    />
                </TbxTooltip>
            </ItemRow>
            <ItemRow>
                <Typography
                    data-testid="nqp-quote-item-number-of-folds-label"
                    sx={classes.itemColumn}
                >
                    {t('Number of folds')}
                </Typography>
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <NumericFormat
                        allowNegative={false}
                        customInput={TextField}
                        decimalScale={0}
                        decimalSeparator={currencyFormat.decimal}
                        disabled={quoteItem?.foldingSource === 'Drawing' || isPartFromLibrary || isCalculatingQuote}
                        inputProps={{
                            style: { fontSize: '14px', textAlign: 'right', paddingBlock: '6px', paddingInline: '12px' },
                            'data-testid': 'nqp-quote-item-max-length-quantity',
                        }}
                        placeholder="0"
                        size="small"
                        sx={classes.formControl_small}
                        thousandSeparator={currencyFormat.group}
                        value={quoteItem?.numberOfFolds ?? ''}
                        variant="outlined"
                        onValueChange={handleNumberOfFoldsChange}
                    />
                </TbxTooltip>
            </ItemRow>
        </Box>
    )
}

ItemFolding.propTypes = {
    quoteItemId: PropTypes.string,
}

export default memo(ItemFolding)
